<template>
  <Loading v-if="loading" />
  <div
    v-else
    class="audio"
  >
    <div class="audio__content">
      <div
        v-for="paragraph in paragraphs"
        :key="paragraph.id"
        class="audio__content__player"
      >
        <div class="player__text">
          <h4 class="text__main-title">
            {{ paragraph.mainTitle }}
          </h4>
          <h4 class="text__main-subtitle">
            {{ paragraph.mainSubtitle }}
          </h4>
          <h4 class="text__title">
            {{ paragraph.title }}
          </h4>
          <p class="text__subtitle">
            {{ paragraph.subtitle }}
          </p>
        </div>

        <div
          v-for="article in paragraph.articles"
          :key="article.id"
          class="player__audio"
        >
          <p class="player__audio__text">
            {{ article.title }}
          </p>

          <audio
            :id="article.id"
            ref="article"
            :src="article.src"
            class="player__audio__audio"
            preload="metadata"
            @loadedmetadata="setDuration(article.id)"
            @timeupdate="setCurrentTime(article.id)"
          />

          <div class="player__audio__controls">
            <button
              :id="article.id"
              class="controls__btn"
              @click="playAudio(article.id)"
            >
              <icon
                :id="`${article.id}-play`"
                class="controls__btn__icon"
                :style="('display: block')"
                icon="Play"
              />
              <icon
                :id="`${article.id}-pause`"
                class="controls__btn__icon"
                :style="('display: none')"
                icon="Pause"
              />
            </button>

            <div class="controls__slider-container">
              <input
                :id="article.id"
                class="controls__slider"
                type="range"
                max="100"
                value="0"
                @input="showRangeProgress"
              >
              <div
                :id="article.id"
                class="controls__progressbar"
              />
            </div>

            <p
              :id="article.id"
              class="controls__currentTime"
            >
              {{ currentTime }}
            </p>
            <span
              :id="article.id"
              class="controls__audioTime"
            >
              {{ duration }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/common/Loading.vue';

export default {
  name: 'Audio',

  components: {
    Loading
  },

  props: {
    paragraphs: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      currentTime: '00:00',
      duration: '00:00',
      loading: false
    };
  },

  methods: {
    playAudio(id) {
      const audio = document.querySelector(`audio#${id}`);

      if (audio.paused) {
        audio.play();
        document.querySelector(`svg#${id}-play`).style.display = 'none';
        document.querySelector(`svg#${id}-pause`).style.display = 'block';
      } else {
        audio.pause();
        document.querySelector(`svg#${id}-play`).style.display = 'block';
        document.querySelector(`svg#${id}-pause`).style.display = 'none';
      }
    },

    calculateTime(secs) {
      const minutes = Math.floor(secs / 60);
      const seconds = Math.floor(secs % 60);
      const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;

      return `${minutes}:${returnedSeconds}`;
    },

    setCurrentTime(id) {
      const audio = document.querySelector(`audio#${id}`);
      const text = document.querySelector(`p#${id}`);
      const progress = document.querySelector(`div#${id}`);

      text.innerText = this.calculateTime(audio.currentTime);

      progress.style.width = `${(audio.currentTime * 100) / audio.duration}%`;
    },

    setDuration(id) {
      const audio = document.querySelector(`audio#${id}`);
      const text = document.querySelector(`span#${id}`);

      text.innerText = this.calculateTime(audio.duration);
    },

    showRangeProgress(id) {
      const audioId = id.currentTarget.getAttribute('id');
      const progress = document.querySelector(`div#${audioId}`);
      const audio = document.querySelector(`audio#${audioId}`);
      const text = document.querySelector(`p#${audioId}`);

      progress.max = Math.floor(audio.duration);
      progress.style.width = `${(id.target.value / id.target.max) * 100}%`;

      text.innerText = this.calculateTime((id.target.value * progress.max) / 100);

      audio.currentTime = (id.target.value * progress.max) / 100;
    }
  }
};
</script>

<style lang="scss" scoped>
.audio {
  &__content {
    height: 80vh;
    overflow-y: scroll;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    padding: 25px;
    background-color: white;
    border-radius: $radius-lg;

    .player__text {
      margin-bottom: 20px;

      .text__main-title {
        font-weight: $font-bold;
        font-size: $text-sm;
      }

      .text__main-subtitle {
        font-weight: $font-medium;
        font-size: $text-sm;
        margin-bottom: 20px;
      }

      .text__title {
        font-weight: $font-bold;
        font-size: $text-sm;
        margin-bottom: 5px;
        text-transform: uppercase;
      }

      .text__subtitle {
        font-weight: $font-medium;
        font-size: $text-sm;
      }
    }

    &__player {
      .player__audio {
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 30px;
        margin-bottom: 40px;

        &__text {
          border-bottom: 1px solid #f2f2f2;
          width: 100%;
          padding-bottom: 10px;
          font-size: $text-sm;
          font-weight: $font-bold;
        }

        &__controls {
          display: grid;
          grid-template-rows: auto auto;
          grid-template-columns: 50px auto 50px;
          gap: 10px;
          column-gap: 15px;
          width: 100%;

          .controls__btn {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 50px;
            width: 50px;
            background-color: $primary-light;
            border-radius: 50%;
          }

          .controls__slider-container {
            position: relative;
            width: 100%;
            align-self: center;
            grid-column: 2/4;

            .controls__slider {
              position: relative;
              height: 3px;
              width: inherit;
              background: #efefef;
              -webkit-appearance: none;

              // <----------- CHROME ----------->
              &::-webkit-slider-thumb {
                -webkit-appearance: none;
                box-sizing: content-box;
                height: 20px;
                width: 15px;
                border-radius: 50%;
                background-color: transparent;
                border: none;
                cursor: auto;
              }

              &::-webkit-slider-runnable-track {
                cursor: pointer;
              }

              // <----------- FIREFOX ----------->
              &::-moz-range-progress {
                background-color: $primary;
                cursor: pointer;
              }

              &::-moz-range-thumb {
                position: relative;
                -webkit-appearance: none;
                box-sizing: content-box;
                height: 20px;
                width: 15px;
                border-radius: 50%;
                background-color: transparent;
                border: none;
                cursor: auto;
              }

              &::-moz-range-track {
                cursor: pointer;
              }
            }

            .controls__progressbar {
              position: absolute;
              content: "";
              bottom: 7px;
              left: 0;
              height: 4px;
              background-color: $primary;
              cursor: auto;
              border-radius: 5px;
            }
          }

          .controls__currentTime {
            grid-row: 2/3;
            grid-column: 2/3;
            font-size: $text-xs;
          }

          .controls__audioTime {
            grid-row: 2/3;
            grid-column: 3/4;
            justify-self: flex-end;
            font-size: $text-xs;
          }
        }
      }
    }
  }
}
</style>
